// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import {
  type Facility,
  type Project,
  type ProjectUpdate,
  type RenderableUser,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePatch,
} from '@evolvemep/api-ts';
import { Button, Flex, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AssigneeSelect } from 'components/Mantine/AssigneeSelect';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { UserInfoBadge } from 'components/Mantine/UserInfoBadge';
import type { StrongOmit, WithRequiredNotNull } from 'helpers/helper-types';
import { EditableDetailItem } from 'modules/Admin/Facilities/FacilityProperties/EditableDetailItem';

export type ProjectPropertiesFormProps = {
  selectedProject: Project;
  onProjectUpdated: (project: Project) => void;
  statusChangeProject: () => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
};

type FormBody = WithRequiredNotNull<StrongOmit<ProjectUpdate, 'projectType' | 'defaultFacilityId' | 'memberIds'>> & {
  projectType: string | null;
  defaultFacility: Pick<Facility, 'facilityId' | 'facilityName'> | null;
  members: RenderableUser[];
};

const toFormBody = (project: Project): FormBody => ({
  projectName: project.projectName,
  projectIdentifier: project.projectIdentifier ?? '',
  projectType: project.projectType,
  defaultFacility: project.defaultFacilityId
    ? { facilityId: project.defaultFacilityId, facilityName: project.defaultFacilityName ?? '' }
    : null,
  members: project.members,
  addressLine1: project.addressLine1 ?? '',
  addressLine2: project.addressLine2 ?? '',
  city: project.city ?? '',
  state: project.state ?? '',
  postalCode: project.postalCode ?? '',
  country: project.country ?? '',
});

const toProjectUpdate = ({ defaultFacility, members, ...values }: FormBody): ProjectUpdate => ({
  ...values,
  defaultFacilityId: defaultFacility?.facilityId ?? null,
  memberIds: members.map((u) => u.userId),
});

export const ProjectPropertiesForm = ({
  selectedProject,
  statusChangeProject,
  onProjectUpdated,
  editing,
  setEditing,
}: ProjectPropertiesFormProps) => {
  const { apiCall: updateCall, loading: saving } = useEvolvePatch('shop/project/:projectId');

  const {
    data: projectTypes = [],
    apiCall: getProjectTypes,
    loading: loadingProjectTypes,
  } = useEvolveGet('shop/project/projectType', { lazy: true });

  const paginatedFacilities = useEvolvePaginatedGet('shop/facility');

  const form = useForm<FormBody>({
    validate: {
      projectName: (n) => !n,
      projectIdentifier: (i) => !i,
    },
  });

  const onSubmit = (values: typeof form.values) => {
    void updateCall(toProjectUpdate(values), { url: `shop/project/${selectedProject.projectId}` }).then(
      (updatedProject) => {
        onProjectUpdated(updatedProject);
        setEditing(false);
      },
    );
  };

  const projectTypeIsNew = !!form.values.projectType && !projectTypes.includes(form.values.projectType);

  return (
    <form onSubmit={form.onSubmit(onSubmit)} style={{ height: '100%', overflowY: 'auto' }}>
      <Flex direction="column">
        <Flex direction="row" gap="sm" mb="lg" justify="flex-end">
          {!editing && (
            <Button variant="light" onClick={statusChangeProject} loading={saving}>
              {selectedProject.isClosed ? 'Activate project' : 'Archive project'}
            </Button>
          )}
          {editing ? (
            <>
              <Button
                variant="light"
                onClick={() => {
                  setEditing(false);
                  form.reset();
                }}
                disabled={saving}
              >
                Cancel edit
              </Button>
              <Button loading={saving} disabled={!form.isDirty()} type="submit">
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="light"
              leftSection={<EvolveIcon icon="Edit" />}
              onClick={() => {
                void getProjectTypes();
                const formValues = toFormBody(selectedProject);
                form.setInitialValues(formValues);
                form.setValues(formValues);
                setEditing(true);
              }}
            >
              Edit
            </Button>
          )}
        </Flex>

        <EditableDetailItem
          name="Project Title"
          editing={editing}
          renderValue={selectedProject.projectName}
          EditComponent={
            <TextInput maxLength={64} size="xs" {...form.getInputProps('projectName')} disabled={saving} />
          }
        />
        <EditableDetailItem
          name="Project Id"
          editing={editing}
          renderValue={selectedProject.projectIdentifier}
          EditComponent={
            <TextInput maxLength={30} size="xs" {...form.getInputProps('projectIdentifier')} disabled={saving} />
          }
        />
        <EditableDetailItem
          name="Project Type"
          editing={editing}
          renderValue={selectedProject.projectType}
          EditComponent={
            <WrappedSelect
              data={projectTypes}
              clearable
              size="xs"
              searchable
              creatable
              createLabel="Create new project type"
              createdValue={projectTypeIsNew ? form.values.projectType : undefined}
              loading={loadingProjectTypes}
              {...form.getInputProps('projectType')}
              onCreate={(v) => form.setFieldValue('projectType', v)}
              disabled={saving}
            />
          }
        />
        <EditableDetailItem name="Status" renderValue={selectedProject.isClosed ? 'Closed' : 'Open'} />
        <EditableDetailItem
          name="Default Facility"
          editing={editing}
          renderValue={selectedProject.defaultFacilityName}
          EditComponent={
            <SearchableSelect<'shop/facility', Facility, 'facilityId'>
              getItemLabel={(i) => i.facilityName}
              idKey="facilityId"
              paginatedGet={paginatedFacilities}
              clearable
              size="xs"
              {...form.getInputProps('defaultFacility')}
              disabled={saving}
            />
          }
        />

        <EditableDetailItem
          name="Project Assignees"
          tooltip="Assigned users receive work order status updates for this project."
          editing={editing}
          renderValue={
            selectedProject.members.length > 0 ? (
              <Flex gap="xs">
                {selectedProject.members.map((u) => (
                  <UserInfoBadge key={u.userId} user={u} />
                ))}
              </Flex>
            ) : (
              '--'
            )
          }
          EditComponent={<AssigneeSelect {...form.getInputProps('members')} disabled={saving} />}
        />

        <EditableDetailItem
          name="Address Line 1"
          editing={editing}
          renderValue={selectedProject.addressLine1}
          EditComponent={
            <TextInput maxLength={64} size="xs" {...form.getInputProps('addressLine1')} disabled={saving} />
          }
        />
        <EditableDetailItem
          name="Address Line 2"
          editing={editing}
          renderValue={selectedProject.addressLine2}
          EditComponent={
            <TextInput maxLength={64} size="xs" {...form.getInputProps('addressLine2')} disabled={saving} />
          }
        />
        <EditableDetailItem
          name="City"
          editing={editing}
          renderValue={selectedProject.city}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('city')} disabled={saving} />}
        />
        <EditableDetailItem
          name="State"
          editing={editing}
          renderValue={selectedProject.state}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('state')} disabled={saving} />}
        />
        <EditableDetailItem
          name="Zip/Postal Code"
          editing={editing}
          renderValue={selectedProject.postalCode}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('postalCode')} disabled={saving} />}
        />
        <EditableDetailItem
          name="Country"
          editing={editing}
          renderValue={selectedProject.country}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('country')} disabled={saving} />}
        />
      </Flex>
    </form>
  );
};
